import React from "react";

export const normalizeTimeValue = (value) => {
  if (!value) return "";
  const [timePart] = value.split(" ");
  const pieces = timePart.split(":");
  if (pieces.length < 2) return value;
  const hours = pieces[0] ? pieces[0].padStart(2, "0") : "";
  const minutes = pieces[1] ? pieces[1].padStart(2, "0") : "00";
  return `${hours}:${minutes}`;
};

// Closing at midnight is represented as 23:59 (same convention as the calendar app).
export const MIDNIGHT_CLOSE = "23:59";

export const clampToHalfHour = (value) => {
  if (!value) return "";
  // 23:59 is a legal closing time — rounding it up would wrap to 00:00
  if (value === MIDNIGHT_CLOSE) return value;
  const [hStr, mStr] = value.split(":");
  let h = parseInt(hStr, 10);
  let m = parseInt(mStr, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return "";

  if (m < 15) m = 0;
  else if (m < 45) m = 30;
  else {
    m = 0;
    h = (h + 1) % 24;
  }

  const hh = h.toString().padStart(2, "0");
  const mm = m.toString().padStart(2, "0");
  return `${hh}:${mm}`;
};

export const buildHalfHourOptions = () => {
  const opts = [];
  for (let h = 0; h < 24; h += 1) {
    ["00", "30"].forEach((m) => {
      opts.push(`${h.toString().padStart(2, "0")}:${m}`);
    });
  }
  return opts;
};

// Closing select additionally offers 23:59 so a business open until midnight can pick it.
export const buildCloseOptions = () => [...buildHalfHourOptions(), MIDNIGHT_CLOSE];

// Display-only 12h formatting; option values and the API payload always stay 24h "HH:MM".
export const formatTimeLabel = (value, uses12HourFormat) => {
  if (!value || !uses12HourFormat) return value;
  const [hStr, mStr] = value.split(":");
  const hours = parseInt(hStr, 10);
  const minutes = parseInt(mStr, 10);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return value;
  const period = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 === 0 ? 12 : hours % 12;
  return `${formattedHours}:${String(minutes).padStart(2, "0")}${period}`;
};

export const getTimeInputLocale = (language) => {
  if (typeof navigator !== "undefined" && navigator.language) {
    return navigator.language;
  }
  return language || "en";
};

export const getUses12HourFormat = (locale) => {
  try {
    return new Intl.DateTimeFormat(locale, {
      hour: "numeric",
    }).resolvedOptions().hour12;
  } catch (err) {
    return false;
  }
};

export const ensureTwoSlots = (slots = []) => {
  const normalized = slots
    .slice(0, 2)
    .map((slot) => ({
      open: clampToHalfHour(normalizeTimeValue(slot.open)),
      close: clampToHalfHour(normalizeTimeValue(slot.close)),
    }));

  while (normalized.length < 2) {
    normalized.push({ open: "", close: "" });
  }

  return normalized;
};

const toMinutes = (timeStr) => {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(":").map((v) => parseInt(v, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

export const validateDay = (day) => {
  if (day.isClosed) {
    return { valid: true, error: "" };
  }

  const slots = ensureTwoSlots(day.slots || []);
  const [slot1, slot2] = slots;
  const s1o = toMinutes(slot1.open);
  const s1c = toMinutes(slot1.close);
  const s2o = toMinutes(slot2.open);
  const s2c = toMinutes(slot2.close);

  if ((s1o === null) !== (s1c === null)) {
    return { valid: false, error: "First interval is incomplete" };
  }
  if (s1o !== null && s1c !== null && s1c <= s1o) {
    return { valid: false, error: "First closing must be after opening" };
  }

  if (day.showSecondSlot) {
    if ((s2o === null) !== (s2c === null)) {
      return { valid: false, error: "Second interval is incomplete" };
    }
    if (s2o !== null && s2c !== null && s2c <= s2o) {
      return { valid: false, error: "Second closing must be after opening" };
    }

    if (s2o !== null && s1c !== null && s2o < s1c) {
      return {
        valid: false,
        error: "Second opening must be after the first interval",
      };
    }
  }

  const hasCompleteSlot =
    (s1o !== null && s1c !== null) ||
    (day.showSecondSlot && s2o !== null && s2c !== null);

  if (!hasCompleteSlot) {
    return { valid: false, error: "Add at least one complete interval" };
  }

  return { valid: true, error: "" };
};

const EditableDaySchedule = ({
  item,
  index,
  t,
  dayLabel,
  uses12HourFormat,
  timeInputLocale,
  toggleDayClosed,
  handleTimeChange,
  onAddSecondSlot,
  onRemoveSecondSlot,
  halfHourOptions,
  closeOptions,
  validationError,
}) => {
  const closingOptions = closeOptions || halfHourOptions;
  const renderSlot = (slot, slotIndex, disabled) => (
    <>
      <div className="time-input-group">
        <label>
          {t("openingHours_openLabel", "Open")} {slotIndex + 1}
        </label>
        <select
          className="time-input select-input"
          value={slot.open}
          onChange={(e) =>
            handleTimeChange(index, slotIndex, "open", e.target.value)
          }
          lang={timeInputLocale}
          disabled={disabled}
        >
          <option value="">{t("openingHours_selectTime", "Select")}</option>
          {halfHourOptions.map((val) => (
            <option key={val} value={val}>
              {formatTimeLabel(val, uses12HourFormat)}
            </option>
          ))}
        </select>
      </div>
      <span className="time-separator">-</span>
      <div className="time-input-group">
        <label>
          {t("openingHours_closeLabel", "Close")} {slotIndex + 1}
        </label>
        <select
          className="time-input select-input"
          value={slot.close}
          onChange={(e) =>
            handleTimeChange(index, slotIndex, "close", e.target.value)
          }
          lang={timeInputLocale}
          disabled={disabled}
        >
          <option value="">{t("openingHours_selectTime", "Select")}</option>
          {closingOptions.map((val) => (
            <option key={val} value={val}>
              {formatTimeLabel(val, uses12HourFormat)}
            </option>
          ))}
        </select>
      </div>
    </>
  );

  return (
    <div
      className="schedule-editor"
      data-hour-format={uses12HourFormat ? "12" : "24"}
    >
      <div className="time-row">
        <div className="day-and-closed">
          <div className="day-label">{dayLabel}</div>
          <label className="closed-toggle inline">
            <input
              type="checkbox"
              checked={item.isClosed}
              onChange={() => toggleDayClosed(index)}
            />
            <span className="closed-label">
              {t("openingHours_closedToggle", "Closed")}
            </span>
          </label>
        </div>

        {item.isClosed ? (
          <div className="closed-status">
            {t("openingHours_closedLabel", "Closed")}
          </div>
        ) : (
          <>
            <div className="time-slots inline">
              {(item.slots || []).map((slot, slotIndex) => {
                const showSlot = slotIndex === 0 || item.showSecondSlot;
                if (!showSlot) return null;
                return (
                  <div className="time-slot" key={slotIndex}>
                    <div className="time-inputs">
                      {renderSlot(slot, slotIndex, item.isClosed)}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="time-slot-actions inline">
              {item.showSecondSlot ? (
                <button
                  type="button"
                  className="icon-btn remove remove-x"
                  onClick={() => onRemoveSecondSlot(index)}
                  disabled={item.isClosed}
                  aria-label={t(
                    "openingHours_removeSecondSlot",
                    "Remove second interval"
                  )}
                >
                  ✕
                </button>
              ) : (
                <button
                  type="button"
                  className="icon-btn add"
                  onClick={() => onAddSecondSlot(index)}
                  disabled={item.isClosed}
                  aria-label={t(
                    "openingHours_addSecondSlot",
                    "Add second interval"
                  )}
                >
                  +
                </button>
              )}
            </div>
          </>
        )}
      </div>
      {validationError && <div className="time-error">{validationError}</div>}
    </div>
  );
};

export default EditableDaySchedule;
